import React, { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const FloatingCTA = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check session dismissal
    try {
      if (sessionStorage.getItem("zk_floating_dismissed") === "1") {
        setDismissed(true);
        return;
      }
    } catch (err) {
      if (typeof console !== "undefined") console.warn("FloatingCTA: sessionStorage read failed, showing CTA normally", err);
    }

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = docHeight > 0 ? scrollTop / docHeight : 0;

      // Find current section to avoid showing on Hero (top) or CTA section (bottom)
      const contactEl = document.getElementById("contacto");
      const inContact = contactEl
        ? contactEl.getBoundingClientRect().top < window.innerHeight && contactEl.getBoundingClientRect().bottom > 0
        : false;

      // Show after 60% scroll, hide on contact section (avoid duplication)
      setVisible(ratio > 0.6 && !inContact);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // Mount-only scroll listener; DOM refs and state setters are stable.
  }, []);

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      sessionStorage.setItem("zk_floating_dismissed", "1");
    } catch (err) {
      if (typeof console !== "undefined") console.warn("FloatingCTA: could not persist dismissal", err);
    }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="floating-cta"
      className={`fixed bottom-5 inset-x-0 z-40 pointer-events-none flex justify-center transition-all duration-500 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
      }`}
      aria-hidden={!visible}
    >
      <div className="pointer-events-auto px-5 max-w-[calc(100%-1rem)]">
        <a
          href="#contacto"
          data-testid="floating-cta-link"
          className="group relative flex items-center gap-3 pl-4 pr-2 py-2 rounded-full bg-[#0D1B2A]/95 backdrop-blur-xl border border-[#00E5A0]/40 shadow-2xl shadow-black/60 hover:border-[#00E5A0] transition-all overflow-hidden"
        >
          {/* Mint glow */}
          <span className="absolute -inset-4 bg-[#00E5A0]/15 blur-2xl rounded-full pointer-events-none" />
          {/* Shine sweep */}
          <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-[#00E5A0]/25 to-transparent -translate-x-full group-hover:translate-x-[400%] transition-transform duration-1000 pointer-events-none" />

          <span className="relative w-1.5 h-1.5 rounded-full bg-[#00E5A0] animate-pulse flex-shrink-0" />
          <span className="relative text-sm text-white whitespace-nowrap">
            <span className="hidden sm:inline">{t.floating.text_a}</span>
            <span className="sm:hidden">✦ {t.floating.cta}</span>
          </span>
          <span className="relative inline-flex items-center gap-1.5 bg-[#00E5A0] text-[#0D1B2A] text-xs font-bold px-3.5 py-2 rounded-full">
            {t.floating.cta}
            <ArrowRight className="w-3 h-3" />
          </span>
          <button
            type="button"
            data-testid="floating-cta-dismiss"
            aria-label={t.floating.close}
            onClick={handleDismiss}
            className="relative w-7 h-7 rounded-full grid place-items-center text-[#6B7A8D] hover:text-white hover:bg-white/10 transition flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </a>
      </div>
    </div>
  );
};

export default FloatingCTA;
